import { ApiResponse, User } from '../types';
import { apiClient } from './apiClient';

interface UpdateProfileRequest {
  full_name: string;
  email: string;
  phone?: string;
  address?: string;
  username?: string;
  shop_name?: string;
}

interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

interface ProfileUpdateResponse {
  success: boolean;
  message: string;
  data: User;
}

interface PasswordChangeResponse {
  success: boolean;
  message: string;
}

class ProfileService {
  async updateProfile(profileData: UpdateProfileRequest, profilePicture?: File): Promise<ApiResponse<User>> {
    try {
      // Always use FormData for consistency
      const formData = new FormData();
      formData.append('full_name', profileData.full_name);
      formData.append('email', profileData.email);
      
      // Only append phone and address if they have values (not empty strings)
      if (profileData.phone && profileData.phone.trim()) {
        formData.append('phone', profileData.phone.trim());
      }
      if (profileData.address && profileData.address.trim()) {
        formData.append('address', profileData.address.trim());
      }
      
      // Add username if provided
      if (profileData.username && profileData.username.trim()) {
        formData.append('username', profileData.username.trim());
      }
      
      // Add shop_name if provided
      if (profileData.shop_name && profileData.shop_name.trim()) {
        formData.append('shop_name', profileData.shop_name.trim());
      }
      
      // Only add profile picture if one is actually provided
      if (profilePicture && profilePicture instanceof File) {
        formData.append('profile_picture', profilePicture);
      }
      
      console.log('Sending profile update with FormData:', {
        full_name: profileData.full_name,
        email: profileData.email,
        phone: profileData.phone,
        address: profileData.address,
        username: profileData.username,
        shop_name: profileData.shop_name,
        has_profile_picture: !!(profilePicture && profilePicture instanceof File)
      });
      
      const response = await apiClient.post<ProfileUpdateResponse>('/user/profile', formData);
      
      console.log('Profile update response:', response);
      
      if (response.success && response.data) {
        // Update local storage with new user data
        localStorage.setItem('user_data', JSON.stringify(response.data));
        
        return {
          success: true,
          data: response.data,
          message: response.message || 'Profile updated successfully'
        };
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error('ProfileService: Update profile error', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      throw error;
    }
  }

  async changePassword(passwordData: ChangePasswordRequest): Promise<ApiResponse<null>> {
    try {
      const response = await apiClient.post<PasswordChangeResponse>('/user/change-password', passwordData);
      
      if (response.success) {
        return {
          success: true,
          data: null,
          message: response.message || 'Password changed successfully'
        };
      } else {
        throw new Error(response.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('ProfileService: Change password error', error);
      throw error;
    }
  }

  async getProfile(): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.get<{ success: boolean; data: User; message: string }>('/user/profile');
      
      if (response.success && response.data) {
        // Update local storage with fresh user data
        localStorage.setItem('user_data', JSON.stringify(response.data));
        
        return {
          success: true,
          data: response.data,
          message: response.message || 'Profile retrieved successfully'
        };
      } else {
        throw new Error(response.message || 'Failed to get profile');
      }
    } catch (error) {
      console.error('ProfileService: Get profile error', error);
      throw error;
    }
  }
}

export const profileService = new ProfileService();